import { useState } from "react";
import { Badge, Banner, Button, ConfirmDialog, Field, Spinner, Table, type Column } from "../../components";
import {
  useChangeMemberRole,
  useCreateInvitation,
  usePracticeInvitations,
  usePracticeMembers,
  useRemoveMember,
  useRevokeInvitation,
} from "../../api/hooks";
import { formatDateTime, type Invitation, type Member, type PracticeRole } from "../../api/types";
import { ApiError } from "../../api/errors";
import { t } from "../../i18n/t";
import { roleLabel } from "./roleLabel";

/**
 * P5-web §2c. OWNER-only mutations (role change, remove, invite, revoke) are gated on `ownerView` — the caller's
 * own role on THIS practice, taken from `PracticeDetailOut.role` by the parent screen — rather than re-derived
 * here, so there is one source of truth for "am I the owner of this practice" and it is the same one the header
 * badge already renders.
 */
export function MembersTab({
  practiceId,
  ownerView,
}: {
  practiceId: string;
  ownerView: boolean;
}): JSX.Element {
  const members = usePracticeMembers(practiceId);
  const invitations = usePracticeInvitations(practiceId);
  const changeRole = useChangeMemberRole(practiceId);
  const removeMember = useRemoveMember(practiceId);
  const createInvitation = useCreateInvitation(practiceId);
  const revokeInvitation = useRevokeInvitation(practiceId);

  const [roleError, setRoleError] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<Member | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<PracticeRole>("STAFF");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitedFlash, setInvitedFlash] = useState<string | null>(null);

  async function onToggleRole(member: Member): Promise<void> {
    setRoleError(null);
    const nextRole: PracticeRole = member.role === "OWNER" ? "STAFF" : "OWNER";
    try {
      await changeRole.mutateAsync({ userId: member.user_id, body: { role: nextRole } });
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("last_owner")) {
        setRoleError(t("practice.members.last_owner_error"));
      } else {
        setRoleError(t("error.unexpected"));
      }
    }
  }

  async function onRemove(): Promise<void> {
    if (pendingRemoval === null) return;
    setRoleError(null);
    try {
      await removeMember.mutateAsync(pendingRemoval.user_id);
      setPendingRemoval(null);
    } catch (caught) {
      setPendingRemoval(null);
      if (caught instanceof ApiError && caught.is("last_owner")) {
        setRoleError(t("practice.members.last_owner_error"));
      } else {
        setRoleError(t("error.unexpected"));
      }
    }
  }

  async function onInvite(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setInviteError(null);
    try {
      const invitation = await createInvitation.mutateAsync({ email: inviteEmail, role: inviteRole });
      setInvitedFlash(t("practice.members.invite_sent", { email: invitation.email }));
      setInviteEmail("");
    } catch {
      setInviteError(t("error.unexpected"));
    }
  }

  const memberColumns: readonly Column<Member>[] = [
    {
      key: "member",
      header: t("practice.members.col_member"),
      render: (row) => (
        <span className="cellvendor">
          <span className="av" aria-hidden="true">
            {row.display_name.slice(0, 2).toUpperCase() || "?"}
          </span>
          <span className="cellvendor__text">
            <span className="cellvendor__name">{row.display_name}</span>
            <span className="cellvendor__sub">{row.email}</span>
          </span>
        </span>
      ),
    },
    {
      key: "role",
      header: t("practice.members.col_role"),
      render: (row) => (
        <Badge tone={row.role === "OWNER" ? "accent" : "neutral"}>{roleLabel(row.role)}</Badge>
      ),
    },
    {
      key: "accepted",
      header: t("practice.members.col_accepted"),
      render: (row) => <span className="mono muted">{formatDateTime(row.accepted_at)}</span>,
    },
    ...(ownerView
      ? [
          {
            key: "actions",
            header: t("practice.members.col_actions"),
            hiddenHeader: true,
            render: (row: Member) => {
              const ownerCount = (members.data?.items ?? []).filter((m) => m.role === "OWNER").length;
              const lastOwner = row.role === "OWNER" && ownerCount <= 1;
              return (
                <span className="cellactions">
                  <Button
                    size="compact"
                    title={lastOwner ? t("practice.members.last_owner_error") : undefined}
                    onClick={() => void onToggleRole(row)}
                    disabled={changeRole.isPending || lastOwner}
                  >
                    {row.role === "OWNER"
                      ? t("practice.members.make_staff")
                      : t("practice.members.make_owner")}
                  </Button>
                  <Button
                    size="compact"
                    variant="destructive"
                    title={lastOwner ? t("practice.members.last_owner_error") : undefined}
                    onClick={() => setPendingRemoval(row)}
                    disabled={removeMember.isPending || lastOwner}
                  >
                    {t("practice.members.remove")}
                  </Button>
                </span>
              );
            },
          },
        ]
      : []),
  ];

  const invitationColumns: readonly Column<Invitation>[] = [
    { key: "email", header: t("practice.members.col_email"), render: (row) => row.email },
    { key: "role", header: t("practice.members.col_role"), render: (row) => roleLabel(row.role) },
    {
      key: "invited_by",
      header: t("practice.members.col_invited_by"),
      render: (row) => row.invited_by,
    },
    {
      key: "expires",
      header: t("practice.members.col_expires"),
      render: (row) => formatDateTime(row.expires_at),
    },
    ...(ownerView
      ? [
          {
            key: "actions",
            header: t("practice.members.col_actions"),
            hiddenHeader: true,
            render: (row: Invitation) => (
              <span className="cellactions">
                <Button
                  size="compact"
                  variant="destructive"
                  onClick={() => void revokeInvitation.mutate(row.invitation_id)}
                  disabled={revokeInvitation.isPending}
                >
                  {t("practice.members.revoke")}
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="splitbody">
      <div className="splitbody__main">
        {roleError === null ? null : <Banner tone="warn">{roleError}</Banner>}
        {invitedFlash === null ? null : (
          <p className="authnote" role="status">
            {invitedFlash}
          </p>
        )}

        <section className="card card--flush" aria-labelledby="members-title">
          <div className="card__header">
            <h2 className="card__title" id="members-title">
              {t("practice.members.caption")}
            </h2>
          </div>
          {members.isPending ? (
            <Spinner />
          ) : (
            <Table
              bare
              caption={t("practice.members.caption")}
              columns={memberColumns}
              rows={members.data?.items ?? []}
              rowKey={(row) => row.user_id}
              emptyMessage={t("practice.members.empty")}
            />
          )}
        </section>

        {ownerView ? (
          <section className="card card--flush" aria-labelledby="invitations-title">
            <div className="card__header">
              <h2 className="card__title" id="invitations-title">
                {t("practice.members.invitations_title")}
              </h2>
            </div>
            {invitations.isPending ? (
              <Spinner />
            ) : (
              <Table
                bare
                caption={t("practice.members.invitations_caption")}
                columns={invitationColumns}
                rows={invitations.data?.items ?? []}
                rowKey={(row) => row.invitation_id}
                emptyMessage={t("practice.members.invitations_empty")}
              />
            )}
          </section>
        ) : null}
      </div>

      {/* PracticeMembers.html: the invite form is the right-hand card, always visible to an OWNER. */}
      {ownerView ? (
        <aside className="splitbody__aside">
          <form
            className="card cancelcard"
            aria-labelledby="invite-title"
            onSubmit={(event) => void onInvite(event)}
          >
            <h2 id="invite-title">{t("practice.members.invite_title")}</h2>
            <Field label={t("practice.members.invite_email")}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                />
              )}
            </Field>
            <fieldset>
              <legend className="field__label">{t("practice.members.invite_role")}</legend>
              <div className="fgrid">
                {(["STAFF", "OWNER"] as const).map((role) => (
                  <label key={role} className="opt">
                    <input
                      type="radio"
                      name="invite_role"
                      value={role}
                      checked={inviteRole === role}
                      onChange={() => setInviteRole(role)}
                    />
                    {roleLabel(role)}
                  </label>
                ))}
              </div>
            </fieldset>
            {inviteError === null ? null : <Banner tone="block">{inviteError}</Banner>}
            <Button
              type="submit"
              variant="primary"
              className="button--auth"
              disabled={createInvitation.isPending || inviteEmail.trim() === ""}
            >
              {t("practice.members.invite_submit")}
            </Button>
          </form>
        </aside>
      ) : null}

      <ConfirmDialog
        open={pendingRemoval !== null}
        title={t("practice.members.remove_confirm_title")}
        destructive
        onConfirm={() => void onRemove()}
        onCancel={() => setPendingRemoval(null)}
      >
        <p>{t("practice.members.remove_confirm_body", { email: pendingRemoval?.email ?? "" })}</p>
      </ConfirmDialog>
    </div>
  );
}
